/**
 * LearnPacer.jsx — sweeps a highlight through a passage at a target
 * WPM. User reads along; the pacer never regresses. Trains the ability
 * to sustain pace and prevents unconscious back-tracking.
 *
 * Target WPM defaults to `progress.pacer_target_wpm` (personal best +
 * 20) so every session is a small stretch, not a wall.
 */
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gauge, Play, Pause, RotateCcw, ArrowLeft, ChevronDown } from 'lucide-react';
import { authFetch } from '../utils/authUtils';
import { readCustomPassage } from '../utils/learnCustomPassage';
import LearnQuiz from '../components/LearnQuiz';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LearnPacer = () => {
  const navigate = useNavigate();
  const [passages, setPassages] = useState([]);
  const [passageIdx, setPassageIdx] = useState(0);
  const [targetWpm, setTargetWpm] = useState(240);
  const [state, setState] = useState('setup'); // setup|running|paused|quiz|done
  const [wordIdx, setWordIdx] = useState(0);
  const startedAt = useRef(0);
  const durationRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    const custom = readCustomPassage();
    if (custom) setPassages([custom]);
    (async () => {
      try {
        if (!custom) {
          const pr = await authFetch(`${BACKEND_URL}/api/learn/passages`);
          if (pr.ok) { const d = await pr.json(); setPassages(d.passages || []); }
        }
        const gp = await authFetch(`${BACKEND_URL}/api/learn/progress`);
        if (gp.ok) { const p = await gp.json(); setTargetWpm(p.pacer_target_wpm || 240); }
      } catch { /* non-fatal */ }
    })();
  }, []);

  const passage = passages[passageIdx];
  const words = React.useMemo(() => (passage ? passage.text.split(/\s+/).filter(Boolean) : []), [passage]);
  const intervalMs = React.useMemo(() => Math.max(80, Math.round(60000 / Math.max(60, targetWpm))), [targetWpm]);

  useEffect(() => {
    if (state !== 'running') return;
    timerRef.current = setInterval(() => {
      setWordIdx((i) => {
        if (i + 1 >= words.length) {
          durationRef.current = Date.now() - startedAt.current;
          clearInterval(timerRef.current);
          setState('quiz');
          return i;
        }
        return i + 1;
      });
    }, intervalMs);
    return () => clearInterval(timerRef.current);
  }, [state, intervalMs, words.length]);

  const start = () => { setWordIdx(0); startedAt.current = Date.now(); setState('running'); };
  const pause = () => { durationRef.current += Date.now() - startedAt.current; setState('paused'); clearInterval(timerRef.current); };
  const resume = () => { startedAt.current = Date.now(); setState('running'); };
  const reset = () => { setState('setup'); setWordIdx(0); durationRef.current = 0; clearInterval(timerRef.current); };

  if (!passage) {
    return (
      <main className="sharp-bg min-h-screen flex items-center justify-center text-white/60" data-testid="learn-pacer-loading">
        Loading passages…
      </main>
    );
  }

  return (
    <main className="sharp-bg min-h-screen max-w-3xl mx-auto px-4 py-6 pb-32" data-testid="learn-pacer-page">
      <button
        onClick={() => navigate('/learn')}
        className="text-xs text-white/50 hover:text-white/80 mb-4 inline-flex items-center gap-1.5"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back to LEARN
      </button>

      <header className="mb-6 flex items-center gap-3">
        <Gauge className="w-6 h-6 text-fuchsia-300" />
        <div>
          <p className="eyebrow text-fuchsia-300 mb-0.5">Drill · Pacer</p>
          <h1 className="font-display text-3xl font-black text-white tracking-tight leading-none">
            Beat the pacer<span className="text-fuchsia-300">.</span>
          </h1>
        </div>
      </header>

      {state === 'setup' && (
        <div className="space-y-5">
          <div className="rounded-2xl bg-[#12131A] border border-white/10 p-5">
            <p className="eyebrow text-white/50 mb-2">Passage</p>
            <div className="relative">
              <select
                value={passageIdx}
                onChange={(e) => setPassageIdx(Number(e.target.value))}
                className="w-full appearance-none bg-black/40 border border-white/15 rounded-lg pl-3 pr-9 py-2.5 text-sm text-white focus:outline-none focus:border-fuchsia-400/60"
                data-testid="learn-pacer-passage-select"
              >
                {passages.map((p, i) => (
                  <option key={p.passage_id} value={i}>
                    {p.title} — {p.author} ({p.word_count} words)
                  </option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
            </div>
          </div>

          <div className="rounded-2xl bg-[#12131A] border border-white/10 p-5">
            <p className="eyebrow text-white/50 mb-2">Target WPM</p>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={150}
                max={600}
                step={10}
                value={targetWpm}
                onChange={(e) => setTargetWpm(Number(e.target.value))}
                className="flex-1 accent-fuchsia-400"
                data-testid="learn-pacer-wpm-slider"
              />
              <span className="w-16 text-right font-display text-2xl font-black text-fuchsia-300 tabular-nums">
                {targetWpm}
              </span>
            </div>
            <p className="text-[11px] text-white/40 mt-2">Auto-set to +20 above your personal best.</p>
          </div>

          <button
            onClick={start}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-fuchsia-500 hover:bg-fuchsia-400 text-white px-4 py-3.5 text-sm font-black tracking-tight transition-colors"
            data-testid="learn-pacer-start"
          >
            <Play className="w-4 h-4" /> Start drill
          </button>
        </div>
      )}

      {(state === 'running' || state === 'paused') && (
        <div className="space-y-5">
          <div
            className="rounded-2xl bg-gradient-to-br from-slate-900 via-slate-950 to-black border border-fuchsia-500/20 p-6 text-white/60 leading-relaxed text-lg"
            data-testid="learn-pacer-stage"
          >
            {words.map((w, i) => (
              <span
                key={i}
                className={`transition-colors duration-100 ${
                  i === wordIdx ? 'text-white bg-fuchsia-500/40 rounded px-1' :
                  i < wordIdx ? 'text-white/40' : 'text-white/70'
                }`}
              >
                {w}{' '}
              </span>
            ))}
          </div>

          <div className="flex items-center gap-3">
            {state === 'running' ? (
              <button
                onClick={pause}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-white/10 hover:bg-white/15 text-white px-4 py-2.5 text-sm font-black"
                data-testid="learn-pacer-pause"
              >
                <Pause className="w-4 h-4" /> Pause
              </button>
            ) : (
              <button
                onClick={resume}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-fuchsia-500 hover:bg-fuchsia-400 text-white px-4 py-2.5 text-sm font-black"
                data-testid="learn-pacer-resume"
              >
                <Play className="w-4 h-4" /> Resume
              </button>
            )}
            <button
              onClick={reset}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-white/[0.03] hover:bg-white/[0.08] border border-white/10 text-white/70 px-4 py-2.5 text-sm font-black"
              data-testid="learn-pacer-reset"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>

          {/* Live pace adjuster — the interval effect re-runs whenever
              `targetWpm` changes, so +/- swaps the sweep speed on the
              next tick without restarting the passage. */}
          <div className="flex items-center gap-2 rounded-xl bg-black/30 border border-fuchsia-500/20 px-3 py-2">
            <span className="text-[10px] uppercase tracking-widest text-fuchsia-300/80 font-black">Pace</span>
            <button
              onClick={() => setTargetWpm((w) => Math.max(150, w - 10))}
              className="w-9 h-9 rounded-lg bg-white/[0.05] hover:bg-white/[0.12] border border-white/10 text-white text-lg font-black leading-none"
              data-testid="learn-pacer-wpm-minus"
              aria-label="Slower"
            >
              −
            </button>
            <span
              className="flex-1 text-center font-display text-2xl font-black text-fuchsia-300 tabular-nums"
              data-testid="learn-pacer-wpm-live"
            >
              {targetWpm}
            </span>
            <button
              onClick={() => setTargetWpm((w) => Math.min(600, w + 10))}
              className="w-9 h-9 rounded-lg bg-white/[0.05] hover:bg-white/[0.12] border border-white/10 text-white text-lg font-black leading-none"
              data-testid="learn-pacer-wpm-plus"
              aria-label="Faster"
            >
              +
            </button>
            <span className="text-[10px] uppercase tracking-widest text-white/40">WPM</span>
          </div>

          <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
            <div
              className="h-full bg-fuchsia-400 transition-all"
              style={{ width: `${words.length ? Math.round(100 * (wordIdx + 1) / words.length) : 0}%` }}
              data-testid="learn-pacer-progress"
            />
          </div>
        </div>
      )}

      {state === 'quiz' && (
        <LearnQuiz
          passage={passage}
          drillKind="pacer"
          durationMs={durationRef.current}
          targetWpm={targetWpm}
          onDone={() => setState('done')}
        />
      )}

      {state === 'done' && (
        <div className="mt-5 flex gap-3">
          <button
            onClick={reset}
            className="flex-1 rounded-lg bg-fuchsia-500 hover:bg-fuchsia-400 text-white px-4 py-3 text-sm font-black"
            data-testid="learn-pacer-again"
          >
            Run another drill
          </button>
          <button
            onClick={() => navigate('/learn')}
            className="rounded-lg bg-white/[0.03] hover:bg-white/[0.08] border border-white/10 text-white/70 px-4 py-3 text-sm font-black"
          >
            Back to LEARN
          </button>
        </div>
      )}
    </main>
  );
};

export default LearnPacer;
